import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Save, Lock, Puzzle, Smartphone, Info } from 'lucide-react';
import { colors, typography, spacing, borderRadius, animation } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import { useUsageStats, usePlans } from '../../hooks/useSubscription';
import { httpClient } from '../../services/http.client';
import { KeycloakService } from '../../services/keycloak.service';

import { SubscriptionCard } from './Subscription/SubscriptionCard';
import { UsageStats } from './Subscription/UsageStats';
import { PlanComparison } from './Subscription/PlanComparison';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { SubscriptionService } from '../../services/subscription.service';

const EXT_BASE = '/agent-v3/api/v3';

export const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const toast = useToast();

  // React Query hooks handle auth gating automatically via KeycloakService.isAuthenticated()
  const { data: usageStats, isLoading: loadingSubscription } = useUsageStats();
  const { data: plansData = [] } = usePlans();

  // Pro tier gate: plan_type >= 4 (Pro/Business/Enterprise) unlocks browser extension.
  // Backend enforces the same rule on POST /extension/keys; this UI guard is courtesy.
  const isPro = (usageStats?.plan?.type ?? 0) >= 4;

  // Initialize from user data
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // UI state for plan comparison modal
  const [showPlanComparison, setShowPlanComparison] = useState(false);

  // API Key state
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyName, setApiKeyName] = useState('Chrome Extension');
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  // Load user data when component mounts or user changes
  useEffect(() => {
    if (user) {
      setUsername(user.email?.split('@')[0] || '');
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleSaveProfile = () => {
    // Save profile logic
    toast.success(t('settings.profileUpdated'));
  };


  const handleUpgradePlan = async (planId: number) => {
    try {
      // Free plan doesn't need Stripe — just no-op for now
      if (planId === 1) {
        toast.info(t('settings.alreadyOnFree') || 'Already on free tier');
        return;
      }
      const { url } = await SubscriptionService.createCheckoutSession(planId, 'month');
      // Redirect user to Stripe-hosted checkout. After payment, Stripe redirects
      // back to /dashboard?upgrade=success and our webhook syncs DB state.
      window.location.href = url;
    } catch (err) {
      console.error('[upgrade] checkout failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to start checkout');
    }
  };

  const handleGenerateApiKey = async () => {
    if (!isAuthenticated) {
      toast.error(t('settings.userNotAuth'));
      return;
    }
    setIsGeneratingKey(true);
    try {
      const res = await httpClient.post<{ success: boolean; data: { api_key: string } }>(
        `${EXT_BASE}/pipeline/extension/keys`,
        { name: apiKeyName.trim() || 'Chrome Extension' }
      );
      if (res.success && res.data?.api_key) {
        setApiKey(res.data.api_key);
        toast.success(t('settings.apiKeyGenerated'));
      } else {
        throw new Error('Failed to generate key');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings.apiKeyFailed'));
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      setApiKeyCopied(true);
      toast.success(t('settings.apiKeyCopied'));
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setApiKeyCopied(false);
        copyTimerRef.current = null;
      }, 2000);
    }
  };

  // Show loading or not authenticated message
  if (!isAuthenticated) {
    return (
      <Container>
        <SectionTitle>{t('settings.title')}</SectionTitle>
        <InfoMessage>
          <Info size={16} strokeWidth={2} aria-hidden="true" /> {t('settings.pleaseLogin')}
        </InfoMessage>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <SectionTitle>{t('settings.title')}</SectionTitle>
        <SectionSubtitle>{t('settings.subtitle')}</SectionSubtitle>

        {user && (
          <UserInfoBanner>
            <UserInfoLabel>{t('settings.loggedInAs')}</UserInfoLabel>
            <UserInfoValue>{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email?.split('@')[0]}</UserInfoValue>
            <UserInfoSubtext>({user.email})</UserInfoSubtext>
          </UserInfoBanner>
        )}
      </PageHeader>

      <SectionsGrid>
        {/* Language Section */}
        <Section>
          <SectionHeader>{t('settings.language')}</SectionHeader>
          <HelperText style={{ marginTop: 0, marginBottom: 16 }}>
            {t('settings.languageDesc')}
          </HelperText>
          <LanguageSwitcher variant="card" />
          <HelperText style={{ marginTop: 12 }}>
            {t('settings.languageCurrent', {
              name: i18n.language?.startsWith('ro')
                ? t('settings.languageRomanian')
                : t('settings.languageEnglish'),
            })}
          </HelperText>
        </Section>

        {/* Profile Section */}
        <Section>
          <SectionHeader>{t('settings.profileInfo')}</SectionHeader>

        <FormGroup>
          <Label htmlFor="username-input">{t('settings.username')}</Label>
          <Input
            id="username-input"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t('settings.enterUsername')}
            disabled
            aria-describedby="username-helper"
          />
          <HelperText id="username-helper">{t('settings.usernameNoChange')}</HelperText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="firstname-input">{t('settings.firstName')}</Label>
          <Input
            id="firstname-input"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('settings.enterFirstName')}
            aria-label={t('settings.firstName')}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lastname-input">{t('settings.lastName')}</Label>
          <Input
            id="lastname-input"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('settings.enterLastName')}
            aria-label={t('settings.lastName')}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email-input">{t('settings.email')}</Label>
          <Input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('settings.enterEmail')}
            aria-label={t('settings.email')}
          />
        </FormGroup>

        <SaveButton onClick={handleSaveProfile} aria-label={t('settings.saveProfile')}>
          <Save size={16} strokeWidth={2} aria-hidden="true" /> {t('settings.saveProfile')}
        </SaveButton>

        <SectionDivider />
        <SectionHeader>{t('settings.changePassword')}</SectionHeader>
        <PasswordDescription>
          {t('settings.changePasswordDesc')}
        </PasswordDescription>
        <SaveButton onClick={() => KeycloakService.changePassword()} aria-label={t('settings.changePassword')}>
          <Lock size={16} strokeWidth={2} aria-hidden="true" /> {t('settings.changePassword')}
        </SaveButton>
      </Section>

      {/* Browser Extension + Mobile App */}
      <Section>
        <SectionHeader>{t('settings.browserExtension')}</SectionHeader>
        <ExtDescription>{t('settings.browserExtensionDesc')}</ExtDescription>

        {!isPro ? (
          <ProLockedCard>
            <ProLockedIcon><Lock size={32} strokeWidth={1.75} /></ProLockedIcon>
            <ProLockedTitle>{t('settings.extProRequired')}</ProLockedTitle>
            <ProLockedDesc>{t('settings.extLockedDesc')}</ProLockedDesc>
            <ViewPlansButton onClick={() => setShowPlanComparison(true)}>
              {t('settings.viewAllPlans')}
            </ViewPlansButton>
          </ProLockedCard>
        ) : (
          <>
            <FormGroup>
              <Label htmlFor="apikey-name-input">{t('settings.keyName')}</Label>
              <Input
                id="apikey-name-input"
                type="text"
                value={apiKeyName}
                onChange={(e) => setApiKeyName(e.target.value)}
                placeholder={t('settings.keyNamePlaceholder')}
              />
            </FormGroup>

            {!apiKey ? (
              <SaveButton onClick={handleGenerateApiKey} disabled={isGeneratingKey}>
                {isGeneratingKey ? t('settings.generating') : t('settings.generateApiKey')}
              </SaveButton>
            ) : (
              <ApiKeyResult>
                <ApiKeyLabel>{t('settings.apiKeyWarning')}</ApiKeyLabel>
                <ApiKeyRow>
                  <ApiKeyValue>{apiKey}</ApiKeyValue>
                  <CopyButton onClick={handleCopyApiKey}>
                    {apiKeyCopied ? t('common.copied') : t('common.copy')}
                  </CopyButton>
                </ApiKeyRow>
                <SaveButton onClick={() => { setApiKey(null); setApiKeyName('Chrome Extension'); }} style={{ marginTop: 12 }}>
                  {t('settings.generateAnother')}
                </SaveButton>
              </ApiKeyResult>
            )}

            <ExtDownloadRow>
              <ExtDownloadInfo>
                <ExtDownloadIcon><Puzzle size={24} strokeWidth={1.75} /></ExtDownloadIcon>
                <div>
                  <ExtDownloadName>{t('settings.extDownloadName')}</ExtDownloadName>
                  <ExtDownloadVersion>{t('settings.extDownloadVersion')}</ExtDownloadVersion>
                </div>
              </ExtDownloadInfo>
              <DownloadButton as="a" href="/downloads/didi-extension-latest.zip" download>
                {t('settings.extDownload')}
              </DownloadButton>
            </ExtDownloadRow>
          </>
        )}

        <SectionDivider />
        <SectionHeader>{t('settings.mobileAppSection')}</SectionHeader>
        <ExtDescription>{t('settings.mobileAppDesc')}</ExtDescription>

        <MobileAppContent>
          <MobileAppIcon><Smartphone size={28} strokeWidth={1.75} /></MobileAppIcon>
          <MobileAppInfo>
            <MobileAppName>{t('settings.didiAndroid')}</MobileAppName>
            <MobileAppVersion>{t('settings.appVersion')}</MobileAppVersion>
          </MobileAppInfo>
        </MobileAppContent>

        <DownloadButton as="a" href="/downloads/didi.apk" download="didi.apk">
          {t('settings.downloadApk')}
        </DownloadButton>
      </Section>

      {/* Subscription & Billing */}
      <Section>
        <SectionHeader>{t('settings.subscriptionBilling')}</SectionHeader>

        {loadingSubscription ? (
          <LoadingMessage>{t('settings.loadingSubscription')}</LoadingMessage>
        ) : usageStats ? (
          <>
            {!showPlanComparison ? (
              <>
                <SubscriptionGrid>
                  <SubscriptionCard
                    data={usageStats}
                    onUpgrade={() => setShowPlanComparison(true)}
                  />
                  <UsageStats stats={usageStats} />
                </SubscriptionGrid>
                {plansData && plansData.length > 1 && (
                  <ViewPlansButton onClick={() => setShowPlanComparison(true)}>
                    {t('settings.viewAllPlans')}
                  </ViewPlansButton>
                )}
              </>
            ) : plansData ? (
              <>
                <BackButton onClick={() => setShowPlanComparison(false)}>
                  {t('settings.backToCurrentPlan')}
                </BackButton>
                <PlanComparison
                  plans={plansData}
                  currentPlanId={usageStats.plan.id}
                  onSelectPlan={handleUpgradePlan}
                />
              </>
            ) : (
              <LoadingMessage>{t('settings.loadingPlans')}</LoadingMessage>
            )}
          </>
        ) : (
          <InfoMessage>{t('settings.failedSubscription')}</InfoMessage>
        )}
      </Section>
      </SectionsGrid>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  animation: fadeIn ${animation.duration.slow} ${animation.easing.easeOut};

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PageHeader = styled.div`
  max-width: 1400px;
  margin: 0 auto ${spacing['3xl']}px;
`;

const SectionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing['2xl']}px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.softWhite};
  margin-bottom: ${spacing.md}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const SectionSubtitle = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  color: #ffffff;
  margin-bottom: ${spacing['3xl']}px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const Section = styled.div`
  background: rgba(139, 92, 246, 0.03);
  border: 1px solid rgba(139, 92, 246, 0.1);
  border-radius: ${borderRadius.lg}px;
  padding: ${spacing['2xl']}px;
  margin-bottom: ${spacing['2xl']}px;
  backdrop-filter: blur(10px);

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.05);
    border-color: rgba(139, 92, 246, 0.2);
  }

  @media (max-width: 480px) {
    padding: ${spacing.md}px;
  }
`;

const SectionHeader = styled.h3`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: #ffffff;
  margin-bottom: ${spacing.xl}px;
  padding-bottom: ${spacing.md}px;
  border-bottom: 1px solid rgba(139, 92, 246, 0.25);

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${spacing.xl}px;
`;

const Label = styled.label`
  display: block;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: #ffffff;
  margin-bottom: ${spacing.sm}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.md}px ${spacing.lg}px;
  background: rgba(139, 92, 246, 0.03);
  border: 1px solid rgba(139, 92, 246, 0.15);
  border-radius: ${borderRadius.md}px;
  color: ${colors.softWhite};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  transition: all ${animation.duration.normal} ${animation.easing.default};

  &::placeholder {
    color: ${colors.steelGray};
  }

  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.5);
    background: rgba(139, 92, 246, 0.08);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(139, 92, 246, 0.01);
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.05);
    border-color: rgba(139, 92, 246, 0.25);
    color: ${colors.slateCharcoal};

    &:focus {
      border-color: rgba(139, 92, 246, 0.5);
      background: rgba(139, 92, 246, 0.08);
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    }

    &:disabled {
      background: rgba(139, 92, 246, 0.03);
    }
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm}px;
  padding: ${spacing.md}px ${spacing['2xl']}px;
  background: #050510;
  border: 2px solid rgba(139, 92, 246, 0.5);
  border-radius: ${borderRadius.md}px;
  color: ${colors.softWhite};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${animation.duration.normal} ${animation.easing.default};
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.2);

  &:hover {
    transform: translateY(-2px);
    background: rgba(139, 92, 246, 0.15);
    border-color: rgba(139, 92, 246, 0.7);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.1);
    border-color: rgba(139, 92, 246, 0.4);
    color: ${colors.slateCharcoal};
    box-shadow: 0 4px 15px rgba(139, 92, 246, 0.15);

    &:hover {
      background: rgba(139, 92, 246, 0.15);
      border-color: rgba(139, 92, 246, 0.6);
      box-shadow: 0 6px 20px rgba(139, 92, 246, 0.2);
    }
  }
`;

const HelperText = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  margin-top: ${spacing.xs}px;
  font-style: italic;

  [data-theme="light"] & {
    color: ${colors.steelGray};
  }
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm}px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: ${borderRadius.md}px;
  padding: ${spacing['2xl']}px;
  color: #ffffff;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  margin-top: ${spacing['3xl']}px;

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.25);
    color: ${colors.slateCharcoal};
  }
`;

const UserInfoBanner = styled.div`
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: ${borderRadius.md}px;
  padding: ${spacing.lg}px ${spacing.xl}px;
  margin-bottom: ${spacing['2xl']}px;
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;

  [data-theme="light"] & {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%);
    border-color: rgba(139, 92, 246, 0.25);
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const UserInfoLabel = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const UserInfoValue = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: #ffffff;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const UserInfoSubtext = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);

  [data-theme="light"] & {
    color: ${colors.steelGray};
  }
`;

// Step 5: Uncomment subscription styled components
const SubscriptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.lg}px;
  margin-bottom: ${spacing.xl}px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: ${spacing['2xl']}px;
    margin-bottom: ${spacing['2xl']}px;
  }
`;

const ViewPlansButton = styled.button`
  width: 100%;
  padding: ${spacing.lg}px;
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: ${borderRadius.md}px;
  color: #ffffff;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${animation.duration.normal} ${animation.easing.default};

  &:hover {
    background: rgba(139, 92, 246, 0.15);
    border-color: rgba(139, 92, 246, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.25);
    color: ${colors.slateCharcoal};

    &:hover {
      background: rgba(139, 92, 246, 0.12);
      border-color: rgba(139, 92, 246, 0.35);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
    }
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm}px;
  padding: ${spacing.md}px ${spacing.lg}px;
  background: #050510;
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: ${borderRadius.md}px;
  color: #ffffff;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  margin-bottom: ${spacing.xl}px;
  transition: all ${animation.duration.normal} ${animation.easing.default};

  &:hover {
    background: rgba(139, 92, 246, 0.1);
    border-color: rgba(139, 92, 246, 0.4);
    color: #ffffff;
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.25);
    color: ${colors.slateCharcoal};

    &:hover {
      background: rgba(139, 92, 246, 0.12);
      border-color: rgba(139, 92, 246, 0.35);
    }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing['3xl']}px;
  color: #ffffff;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const SectionDivider = styled.div`
  border-top: 1px solid rgba(139, 92, 246, 0.15);
  margin: ${spacing['2xl']}px 0;
`;

const PasswordDescription = styled.p`
  font-size: ${typography.fontSize.sm}; color: rgba(255,255,255,0.6);
  margin-bottom: ${spacing.xl}px; line-height: 1.5;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

/* Browser Extension */
const ExtDescription = styled.p`
  font-size: ${typography.fontSize.sm}; color: rgba(255,255,255,0.6);
  margin-bottom: ${spacing.xl}px; line-height: 1.5;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
const ApiKeyResult = styled.div`margin-top: ${spacing.md}px;`;
const ApiKeyLabel = styled.div`
  font-size: ${typography.fontSize.sm}; color: #fbbf24; font-weight: 600;
  margin-bottom: ${spacing.sm}px;
`;
const ApiKeyRow = styled.div`
  display: flex; align-items: center; gap: ${spacing.sm}px;
  background: rgba(0,0,0,0.3); border: 1px solid rgba(139,92,246,0.2);
  border-radius: ${borderRadius.md}px; padding: ${spacing.md}px;
  [data-theme="light"] & { background: rgba(139,92,246,0.04); }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const ApiKeyValue = styled.code`
  flex: 1; font-size: 13px; color: #a78bfa; word-break: break-all;
  font-family: monospace;
  [data-theme="light"] & { color: ${colors.neonViolet}; }
`;
const CopyButton = styled.button`
  flex-shrink: 0; padding: 6px 16px; border: 1px solid rgba(139,92,246,0.3);
  border-radius: ${borderRadius.sm}px; background: rgba(139,92,246,0.1);
  color: #a78bfa; font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(139,92,246,0.2); border-color: rgba(139,92,246,0.5); }
  [data-theme="light"] & { color: ${colors.neonViolet}; }
`;

/* Mobile App */
const MobileAppContent = styled.div`
  display: flex; align-items: center; gap: ${spacing.lg}px;
  padding: ${spacing.lg}px;
  background: rgba(139,92,246,0.04);
  border: 1px solid rgba(139,92,246,0.1);
  border-radius: ${borderRadius.md}px;
  margin-bottom: ${spacing.xl}px;
  [data-theme="light"] & { background: rgba(139,92,246,0.03); border-color: rgba(139,92,246,0.12); }
`;
const MobileAppIcon = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; flex-shrink: 0;
  background: rgba(139,92,246,0.12); border-radius: 10px;
  color: ${colors.neonViolet};
  [data-theme="light"] & { background: rgba(139,92,246,0.1); }
`;
const MobileAppInfo = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`;
const MobileAppName = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold}; color: #ffffff;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
const MobileAppVersion = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.5);
  [data-theme="light"] & { color: #9CA3AF; }
`;
const DownloadButton = styled.button`
  display: inline-flex; align-items: center; gap: ${spacing.sm}px;
  padding: ${spacing.md}px ${spacing['2xl']}px;
  background: #050510; border: 2px solid rgba(139,92,246,0.5);
  border-radius: ${borderRadius.md}px; color: ${colors.softWhite};
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold}; cursor: pointer;
  transition: all ${animation.duration.normal} ${animation.easing.default};
  box-shadow: 0 4px 15px rgba(139,92,246,0.2);
  text-decoration: none;
  &:hover:not(:disabled) {
    transform: translateY(-2px); background: rgba(139,92,246,0.15);
    border-color: rgba(139,92,246,0.7); box-shadow: 0 6px 20px rgba(139,92,246,0.3);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  [data-theme="light"] & {
    background: rgba(139,92,246,0.1); border-color: rgba(139,92,246,0.4);
    color: ${colors.slateCharcoal}; box-shadow: 0 4px 15px rgba(139,92,246,0.15);
  }
`;

/* Browser Extension — Pro-locked card (free tier) */
const ProLockedCard = styled.div`
  display: flex; flex-direction: column; align-items: center;
  text-align: center; padding: ${spacing['2xl']}px;
  background: rgba(139,92,246,0.04);
  border: 1px dashed rgba(139,92,246,0.3);
  border-radius: ${borderRadius.md}px;
  [data-theme="light"] & { background: rgba(139,92,246,0.05); }
`;
const ProLockedIcon = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  width: 56px; height: 56px; margin-bottom: ${spacing.md}px;
  background: rgba(139,92,246,0.1); border-radius: 50%;
  color: ${colors.neonViolet};
  [data-theme="light"] & { background: rgba(139,92,246,0.08); }
`;
const ProLockedTitle = styled.h4`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg}; font-weight: ${typography.fontWeight.semibold};
  color: ${colors.softWhite}; margin-bottom: ${spacing.sm}px;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
const ProLockedDesc = styled.p`
  font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.6); line-height: 1.5;
  margin-bottom: ${spacing.lg}px; max-width: 380px;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

/* Browser Extension — download row (Pro tier) */
const ExtDownloadRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap;
  gap: ${spacing.md}px; margin-top: ${spacing.xl}px;
  padding: ${spacing.lg}px;
  background: rgba(139,92,246,0.04);
  border: 1px solid rgba(139,92,246,0.1);
  border-radius: ${borderRadius.md}px;
  [data-theme="light"] & { background: rgba(139,92,246,0.03); border-color: rgba(139,92,246,0.12); }
  @media (max-width: 480px) {
    flex-direction: column; align-items: stretch;
  }
`;
const ExtDownloadInfo = styled.div`
  display: flex; align-items: center; gap: ${spacing.md}px;
  flex: 1 1 auto; min-width: 0;
`;
const ExtDownloadIcon = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; flex-shrink: 0;
  background: rgba(139,92,246,0.12); border-radius: 10px;
  color: ${colors.neonViolet};
  [data-theme="light"] & { background: rgba(139,92,246,0.1); }
`;
const ExtDownloadName = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold}; color: #ffffff;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
const ExtDownloadVersion = styled.div`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.5);
  [data-theme="light"] & { color: #9CA3AF; }
`;
